import { useTranslate } from 'react-polyglot';
import { observer } from 'mobx-react';

import type { PlayerData } from '@genially/p2p-lib';

import { useModal } from '../../../../../../bootstrap/application/components/app/components/ModalProvider/ModalProvider.context';
import { KickPlayerConfirmModal } from '../../../../../../shared/application/components/KickPlayerConfirmModal/KickPlayerConfirmModal';
import { PlayersList } from '../../../../../../shared/application/components/PlayersList/PlayersList';
import { useNotificationManager } from '../../../../../../shared/application/services/useNotificationManager';
import { useHostClientContext } from '../HostClientProvider/hooks/useHostClientContext';
import { IconWrapper, StyledArrowRightToBracketIcon } from './Players.styled';

interface PlayersProps {
  direction?: (typeof PlayersList.Direction)[keyof typeof PlayersList.Direction];
  playerWidthFull?: boolean;
  forcedPlayerSize?: (typeof PlayersList.PlayerSize)[keyof typeof PlayersList.PlayerSize];
  centerAligned?: boolean;
  onParticipantClick?: (player: PlayerData) => void;
}

const PlayersInternal = ({
  direction,
  playerWidthFull,
  forcedPlayerSize,
  centerAligned,
  onParticipantClick,
}: PlayersProps) => {
  const t = useTranslate();

  const { openModal, closeModal } = useModal();
  const { client } = useHostClientContext();
  const { notificationManager } = useNotificationManager();

  const players = client.players.toReversed();

  const kickPlayer = (player: PlayerData) => () => {
    client.kickPlayer(player.id);
    notificationManager.success(t('__new.play.expelPlayer.notification.success'));
  };

  const handlePlayerClick = (player: PlayerData) => {
    onParticipantClick?.(player);

    openModal(parentElement => (
      <KickPlayerConfirmModal
        parentElement={parentElement}
        closeModal={closeModal}
        playerUsername={player.username}
        handleKickPlayer={kickPlayer(player)}
      />
    ));
  };

  return (
    <PlayersList
      players={players}
      Icon={
        <IconWrapper>
          <StyledArrowRightToBracketIcon />
        </IconWrapper>
      }
      dangerOnHover
      direction={direction}
      playerWidthFull={playerWidthFull}
      forcedPlayerSize={forcedPlayerSize}
      centerAligned={centerAligned}
      onPlayerClick={handlePlayerClick}
    />
  );
};

PlayersInternal.Direction = PlayersList.Direction;
PlayersInternal.PlayerSize = PlayersList.PlayerSize;

export const Players = observer(PlayersInternal);
